/**
 * Render PDF pages to PNG blobs in the browser.
 *
 * Used by the SLD upload flow. SLDs have no parser, so nothing server-side ever
 * produces a page raster for them — and the web tier has no PDF rasterizer at
 * all (generation lives in the Python tier, which local mode does not run).
 * The browser already ships `pdfjs-dist` and renders PDF pages to canvas in the
 * viewer, the region picker and the tag-mapping canvas, so doing it here needs
 * no new dependency and works with the stack the developer actually has up.
 *
 * Extracted from the render block in `routes/DataCatalog/RegionSelectionModal.tsx`
 * so the two cannot drift on scale or encoding.
 */

import { getDocument } from 'pdfjs-dist'
import '@/lib/pdfWorkerLoader'

/**
 * Target render resolution, in DPI.
 *
 * pdfjs scales relative to 72 DPI, so the region picker's `scale: 2` is only
 * 144 DPI — BELOW the 200 DPI the P&ID page pipeline renders at
 * (`fitz.Matrix(200/72, …)`), and well below what a scanned one-line needs for
 * a device number like `52-101A` to stay legible. Worse, a scan embedded at
 * 300 DPI gets DOWNSAMPLED at 144.
 *
 * 300 matches normal engineering-drawing scan resolution, so a scanned source
 * is reproduced rather than reduced.
 */
export const TARGET_RASTER_DPI = 300

/**
 * A long edge to render a small sheet up to, in pixels, for callers that ask.
 *
 * The graph editor draws every outline at a FIXED width in image pixels — 8 px
 * for a symbol outline or a line (`PnidRenderer.tsx`) — tuned on P&ID page
 * rasters, which are 11811x8250 px on this repo's P&IDs. An SLD drawn on a
 * letter-size PDF page is only 3300 px wide at 300 DPI, so those same strokes
 * cover 3.6x as much of the drawing and bury its text (VISN-304). Rendering
 * small sheets up to P&ID scale keeps the editor's strokes in proportion
 * without changing the editor every P&ID uses.
 */
export const PNID_SCALE_LONG_EDGE_PX = 10_000

/** pdfjs renders relative to 72 DPI. */
const PDF_POINTS_PER_INCH = 72

/**
 * Hard ceilings on one rendered page.
 *
 * A large sheet at 300 DPI is enormous: E-size (34x44in) is 10200x13200 =
 * 135 Mpx. Browsers refuse a canvas beyond ~16384px per side and run out of
 * memory well before the area limit, so the DPI is reduced for pages that
 * would exceed either — a slightly softer render beats a blank canvas.
 */
const MAX_RASTER_EDGE_PX = 16_000
const MAX_RASTER_PIXELS = 80_000_000

/**
 * The scale to render this page at: `TARGET_RASTER_DPI`, or more for a sheet
 * whose long edge would fall short of `minLongEdgePx`, reduced only as far as
 * the ceilings above require. The area ceiling can leave a near-square sheet's
 * long edge short of `minLongEdgePx` (17x17 in stops at about 8900 px).
 */
export function rasterScaleFor(widthPt: number, heightPt: number, minLongEdgePx = 0): number {
  const target = Math.max(
    TARGET_RASTER_DPI / PDF_POINTS_PER_INCH,
    minLongEdgePx / Math.max(widthPt, heightPt),
  )
  const byEdge = MAX_RASTER_EDGE_PX / Math.max(widthPt, heightPt)
  const byArea = Math.sqrt(MAX_RASTER_PIXELS / (widthPt * heightPt))
  return Math.min(target, byEdge, byArea)
}

export interface RasterizeOptions {
  /** Override the computed scale. Normally left unset — see `rasterScaleFor`. */
  scale?: number
  /** Render a small sheet up to this long edge, in pixels. See `PNID_SCALE_LONG_EDGE_PX`. */
  minLongEdgePx?: number
  signal?: AbortSignal
  /** Called after each page renders, for upload progress. */
  onProgress?: (done: number, total: number) => void
}

/** Render one already-loaded PDF page to a PNG blob. */
async function renderPageToBlob(
  pdf: Awaited<ReturnType<typeof getDocument>['promise']>,
  pageNum: number,
  scale?: number,
  minLongEdgePx?: number,
): Promise<Blob> {
  const page = await pdf.getPage(pageNum)
  // Compute the scale per PAGE, not once per document: a set can mix sheet
  // sizes, and the ceilings apply to whichever page is largest.
  const base = page.getViewport({ scale: 1 })
  const viewport = page.getViewport({
    scale: scale ?? rasterScaleFor(base.width, base.height, minLongEdgePx),
  })
  const canvas = document.createElement('canvas')
  canvas.width = viewport.width
  canvas.height = viewport.height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Could not get a 2D canvas context')

  await page.render({ canvasContext: context, viewport, canvas }).promise

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error(`toBlob failed for page ${pageNum}`))),
      'image/png',
    )
  })
}

/**
 * Rasterize every page of a PDF, in page order.
 *
 * Pages render SEQUENTIALLY, not in parallel: a full-size one-line at scale 2
 * is a large canvas, and rendering a whole multi-page set at once is what makes
 * a browser tab run out of memory. Sequential is slower and survives.
 */
export async function rasterizePdf(
  file: Blob,
  options: RasterizeOptions = {},
): Promise<Blob[]> {
  const { scale, minLongEdgePx, signal, onProgress } = options

  const data = new Uint8Array(await file.arrayBuffer())
  const pdf = await getDocument({ data }).promise

  try {
    const pages: Blob[] = []
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
      pages.push(await renderPageToBlob(pdf, pageNum, scale, minLongEdgePx))
      onProgress?.(pageNum, pdf.numPages)
    }
    return pages
  } finally {
    // Release the worker's copy of the document even if a page threw — without
    // this a failed multi-page render leaks the whole parsed PDF.
    await pdf.destroy()
  }
}

/**
 * Re-encode an image blob as PNG via the canvas.
 *
 * Needed because what we store is served back with a content type inferred from
 * the stored path's extension (`previews.service.ts` `IMAGE_CONTENT_TYPES`).
 * Passing a TIFF or JPEG through unchanged would save foreign bytes under a
 * `.png` key, and the browser would then be handed `image/png` for something
 * that is not one — a blank canvas with no error.
 *
 * TIFF is the case that forces this: it is in the upload accept list and no
 * browser renders it in an `<img>`, so `createImageBitmap` is what decides
 * whether a given file is usable at all. A file it cannot decode throws here,
 * which surfaces as a failed upload rather than an SLD that opens blank.
 */
async function reencodeAsPng(file: File): Promise<Blob> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch (cause) {
    throw new Error(
      `${file.name}: this image format (${file.type || 'unknown'}) cannot be decoded by the browser — convert it to PNG or PDF first`,
      { cause },
    )
  }
  try {
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Could not get a 2D canvas context')
    context.drawImage(bitmap, 0, 0)
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error(`toBlob failed for ${file.name}`))),
        'image/png',
      )
    })
  } finally {
    bitmap.close()
  }
}

/**
 * Page rasters for any drawing file, always as PNG.
 *
 * A PDF is rendered page by page. An image file is re-encoded to PNG — see
 * {@link reencodeAsPng} for why passing it through untouched is not safe. An
 * image that is already PNG is returned as-is, since re-encoding it would only
 * cost time.
 */
export async function rasterizeDrawing(
  file: File,
  options: RasterizeOptions = {},
): Promise<Blob[]> {
  if (file.type.startsWith('image/')) {
    const page = file.type === 'image/png' ? file : await reencodeAsPng(file)
    options.onProgress?.(1, 1)
    return [page]
  }
  return rasterizePdf(file, options)
}
